import { useState, useRef, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { SquareLoader } from "@/components/ui/square-loader";
import { Icon } from "@/components/ui/icon";
import { PageHeader } from "@/components/layout/PageHeader";
import { chat as chatApi, competitors as competitorsApi } from "@/lib/api";
import type { Competitor } from "@/types";
import { cn, timeAgo } from "@/lib/utils";
import { ChatMessages } from "@/components/chat/ChatMessages";
import type { ChatMessage } from "@/components/chat/ChatMessages";
import { useWorkspaceRefresh } from "@/hooks/useWorkspaceRefresh";

// ─── Types ────────────────────────────────────────────────────────────────────

// Re-export ChatMessage as UIMessage for internal use
type UIMessage = ChatMessage;


interface Session {
  id: string;
  domain: string;
  title: string;
  message_count: number;
  created_at: string;
  updated_at: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const WORKER_BASE = (import.meta as { env?: { VITE_API_URL?: string } }).env?.VITE_API_URL ?? "/api";
const AGENT_NAME_IN_URL = "chat-agent";

/** The agent lives on the same host as the API (ws: locally through the Vite proxy). */
function agentSocketBase(): string {
  const api = new URL(WORKER_BASE, window.location.href);
  return `${api.protocol === "http:" ? "ws:" : "wss:"}//${api.host}`;
}




// ─── AIChatAgent WebSocket client ─────────────────────────────────────────────
// Uses the CF_AGENT_USE_CHAT_REQUEST protocol from @cloudflare/ai-chat

class AIChatClient {
  private ws: WebSocket | null = null;
  private closed = false;
  private retryCount = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly MAX_RETRIES = 5;
  private instanceName: string;

  onMessages?: (messages: UIMessage[]) => void;
  onStreaming?: (messageId: string, delta: string) => void;
  onStatusChange?: (status: "idle" | "streaming" | "error") => void;
  onToolCall?: (toolName: string, args: Record<string, unknown>) => void;
  onToolResult?: (toolName: string, result: unknown) => void;
  onError?: (err: Error) => void;

  constructor(instanceName: string) {
    this.instanceName = instanceName;
  }

  connect() {
    if (this.closed || this.ws) return;
    const url = `${agentSocketBase()}/agents/${AGENT_NAME_IN_URL}/${encodeURIComponent(this.instanceName)}`;

    try {
      this.ws = new WebSocket(url);
    } catch {
      this.scheduleReconnect();
      return;
    }

    this.ws.onopen = () => {
      this.retryCount = 0;
      if (this.reconnectTimer) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null; }
      // Request existing messages and any active stream on connect
      this.ws?.send(JSON.stringify({ type: "cf_agent_stream_resume_request" }));
    };

    this.ws.onmessage = (event: MessageEvent) => {
      try {
        const msg = JSON.parse(event.data as string) as {
          type: string;
          messages?: UIMessage[];
          id?: string;
          body?: string;        // JSON string containing the stream chunk
          done?: boolean;
          error?: boolean;      // body is then an error message, not a chunk
          finishReason?: string;
        };

        // Full message list broadcast (after turn completes or on connect)
        if (msg.type === "cf_agent_chat_messages" && msg.messages) {
          this.onMessages?.(msg.messages);
          this.onStatusChange?.("idle");
        }

        // Streaming response chunk — body is a JSON string
        else if (msg.type === "cf_agent_use_chat_response" && msg.id) {
          if (msg.error) {
            this.onError?.(new Error(msg.body || "Mira couldn't finish that answer."));
            this.onStatusChange?.("idle");
          } else if (msg.done) {
            this.onStatusChange?.("idle");
          } else if (msg.body) {
            try {
              const chunk = JSON.parse(msg.body) as {
                type: string;
                delta?: string;
                textDelta?: string;
                // tool-call chunk fields
                toolCallId?: string;
                toolName?: string;
                argsTextDelta?: string;
                args?: Record<string, unknown>;
                result?: unknown;
              };
              const text = chunk.delta ?? chunk.textDelta;
              if (chunk.type === "text-delta" && text) {
                this.onStreaming?.(msg.id, text);
              } else if (chunk.type === "tool-call" && chunk.toolName) {
                // Tool is being called — notify with args
                this.onToolCall?.(chunk.toolName, chunk.args ?? {});
              } else if (chunk.type === "tool-result" && chunk.toolName) {
                this.onToolResult?.(chunk.toolName, chunk.result);
              }
            } catch {
              // ignore unparseable chunks
            }
          }
        }

        // Clear confirmation
        else if (msg.type === "cf_agent_chat_clear") {
          this.onMessages?.([]);
          this.onStatusChange?.("idle");
        }
        // ignore cf_agent_identity, cf_agent_mcp_servers, cf_agent_state etc.
      } catch {
        // ignore
      }
    };

    this.ws.onerror = () => {
      this.onError?.(new Error("WebSocket error"));
    };

    this.ws.onclose = () => {
      this.ws = null;
      if (!this.closed) this.scheduleReconnect();
    };
  }

  private scheduleReconnect() {
    if (this.closed || this.reconnectTimer) return;
    if (this.retryCount >= this.MAX_RETRIES) {
      this.onError?.(new Error("Can't reach Mira. Check your connection and reload the page."));
      this.onStatusChange?.("idle");
      return;
    }
    const delay = Math.min(2000 * Math.pow(2, this.retryCount), 32_000);
    this.retryCount++;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  sendMessage(text: string, body?: Record<string, unknown>) {
    const requestId = crypto.randomUUID();

    // AIChatAgent expects: { type: "cf_agent_use_chat_request", id, init: { method: "POST", body: JSON.stringify({messages, ...customBody}) } }
    const messages = [
      {
        id: crypto.randomUUID(),
        role: "user",
        parts: [{ type: "text", text }],
        createdAt: new Date().toISOString(),
      },
    ];

    const payload = JSON.stringify({
      type: "cf_agent_use_chat_request",
      id: requestId,
      init: {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages,
          ...(body ?? {}),
        }),
      },
    });

    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(payload);
      this.onStatusChange?.("streaming");
    } else {
      const onOpen = () => {
        this.ws?.send(payload);
        this.onStatusChange?.("streaming");
        this.ws?.removeEventListener("open", onOpen);
      };
      if (this.ws) {
        this.ws.addEventListener("open", onOpen);
      } else {
        this.connect();
        setTimeout(() => this.sendMessage(text, body), 600);
      }
    }
  }

  clearHistory() {
    this.ws?.send(JSON.stringify({ type: "cf_agent_chat_clear" }));
  }

  close() {
    this.closed = true;
    if (this.reconnectTimer) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null; }
    this.ws?.close();
    this.ws = null;
  }
}

const normalizeDomain = (d: string) => d.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/^www\./, "").split("/")[0];

// ─── Scraping steps ───────────────────────────────────────────────────────────

const SCRAPE_STEPS = [
  { id: "gather",  label: "Gathering information",  icon: "radar-01" },
  { id: "compile", label: "Compiling content",       icon: "file-01" },
  { id: "ready",   label: "Getting ready to chat",   icon: "magic-wand-01" },
] as const;

type ScrapeStepId = typeof SCRAPE_STEPS[number]["id"];

// ─── Component ────────────────────────────────────────────────────────────────

export function ChatPage() {
  const { sessionId: urlSessionId } = useParams<{ sessionId?: string }>();
  const navigate = useNavigate();
  // /chat?competitor=acme.ai (from the Overview) opens a chat about that competitor
  const [searchParams] = useSearchParams();
  const requestedCompetitor = searchParams.get("competitor");
  const openedRequestedRef = useRef(false);

  // ── Competitor state ──────────────────────────────────────────────────────
  const [competitors, setCompetitors] = useState<Competitor[]>([]);
  const [selectedCompetitor, setSelectedCompetitor] = useState<Competitor | null>(null);
  const [showCompetitorPicker, setShowCompetitorPicker] = useState(false);
  const [scrapeStep, setScrapeStep] = useState<ScrapeStepId | null>(null);
  const [isLoadingContext, setIsLoadingContext] = useState(false);
  const [chatContext, setChatContext] = useState("");
  // Domain of the open session; resolved to a competitor once both have loaded
  const [sessionDomain, setSessionDomain] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(true);
  // Live tool activity during streaming
  const [liveTools, setLiveTools] = useState<Array<{ toolName: string; args: Record<string, unknown>; done: boolean }>>([]);

  const [messages, setMessages] = useState<UIMessage[]>([]);
  const [streamingMsg, setStreamingMsg] = useState<{ id: string; text: string } | null>(null);
  const [input, setInput] = useState("");
  const [status, setStatus] = useState<"idle" | "streaming" | "error">("idle");
  const [sessions, setSessions] = useState<Session[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [sessionLoading, setSessionLoading] = useState(!!urlSessionId);
  const [activeSessionId, setActiveSessionId] = useState<string | undefined>(urlSessionId);
  const [chatUsage, setChatUsage] = useState<{ used: number; limit: number | null; remaining: number | null; unlimited: boolean; plan: string } | null>(null);
  const activeSessionIdRef = useRef<string | undefined>(urlSessionId);

  // Keep URL in sync with active session — only push when we have an ID
  const prevSessionIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (activeSessionId && activeSessionId !== prevSessionIdRef.current) {
      prevSessionIdRef.current = activeSessionId;
      navigate(`/chat/${activeSessionId}`, { replace: true });
    } else if (!activeSessionId && prevSessionIdRef.current) {
      prevSessionIdRef.current = undefined;
      navigate("/chat", { replace: true });
    }
  }, [activeSessionId, navigate]);
  const clientRef = useRef<AIChatClient | null>(null);

  // Load data when workspace changes
  const loadData = useCallback(async () => {
    // Clear all chat state from previous workspace
    setActiveSessionId(undefined);
    activeSessionIdRef.current = undefined;
    setMessages([]);
    setStreamingMsg(null);
    setStatus("idle");
    setLiveTools([]);
    setSelectedCompetitor(null);
    setSessionDomain(null);
    setChatContext("");
    
    // Close existing WebSocket connection
    if (clientRef.current) {
      clientRef.current.close();
      clientRef.current = null;
    }

    setSessionsLoading(true);
    
    const [competitorsRes, sessionsRes, usageRes] = await Promise.all([
      competitorsApi.list(),
      chatApi.sessions(),
      chatApi.usage(),
    ]);

    if ("data" in competitorsRes && competitorsRes.data) {
      setCompetitors(competitorsRes.data);
    }
    
    if ("data" in sessionsRes && sessionsRes.data) {
      setSessions(sessionsRes.data as Session[]);
    }
    
    if ("data" in usageRes && usageRes.data) {
      setChatUsage(usageRes.data);
    }
    
    setSessionsLoading(false);
  }, []);

  useWorkspaceRefresh(loadData);

  // Cleanup on unmount
  useEffect(() => {
    return () => { clientRef.current?.close(); clientRef.current = null; };
  }, []);

  const getOrCreateClient = useCallback((sessionId: string) => {
    // Each session gets its own agent instance (its own Durable Object), which
    // the worker only opens for members of the session's workspace
    const instanceName = `chat-session-${sessionId}`;

    // If we already have a client for this instance, reuse it
    if (clientRef.current) {
      // Check if it's the same instance — if not, close and recreate
      const currentName = (clientRef.current as unknown as { instanceName: string }).instanceName;
      if (currentName === instanceName) {
        return clientRef.current;
      }
      clientRef.current.close();
      clientRef.current = null;
    }

    const client = new AIChatClient(instanceName);
    client.onMessages = (msgs) => {
      setMessages(prev => {
        // Never let an empty broadcast wipe existing messages
        if (msgs.length === 0 && prev.length > 0) return prev;

        // Remove optimistic messages whose text matches a real message in the agent's list
        // (the agent has persisted them with a proper ID)
        const agentTexts = new Set(
          msgs
            .filter((m: { role: string }) => m.role === "user")
            .map((m: { content?: string; parts?: Array<{ type: string; text?: string }> }) => {
              if (typeof m.content === "string") return m.content.trim();
              return (m.parts ?? []).filter((p: { type: string }) => p.type === "text").map((p: { text?: string }) => p.text ?? "").join("").trim();
            })
        );

        const pendingOptimistic = prev.filter(m => {
          if (!m.id.startsWith("opt-")) return false;
          const text = (m.parts ?? []).filter(p => p.type === "text").map(p => (p as { text?: string }).text ?? "").join("").trim();
          // Keep only if the agent hasn't confirmed this text yet
          return !agentTexts.has(text);
        });

        return pendingOptimistic.length > 0
          ? [...msgs, ...pendingOptimistic]
          : msgs;
      });
      setStreamingMsg(null);
      setStatus("idle");
      setLiveTools([]); // clear tool activity when turn completes
    };
    client.onStreaming = (id, delta) => {
      setStreamingMsg(prev => prev?.id === id
        ? { id, text: prev.text + delta }
        : { id, text: delta }
      );
      setStatus("streaming");
    };
    client.onStatusChange = (s) => {
      setStatus(s);
      if (s === "idle") setLiveTools([]);
    };
    client.onToolCall = (toolName, args) => {
      setLiveTools(prev => [...prev, { toolName, args, done: false }]);
    };
    client.onToolResult = (toolName) => {
      setLiveTools(prev => prev.map(t =>
        t.toolName === toolName && !t.done ? { ...t, done: true } : t
      ));
    };
    client.onError = (err) => {
      setStreamingMsg(null);
      setStatus("idle");
      setLiveTools([]);
      toast.error(err.message);
    };
    client.connect();
    clientRef.current = client;
    return client;
  }, []);

  // ── Select competitor and load context ─────────────────────────────────────

  const handleSelectCompetitor = async (competitor: Competitor) => {
    setSelectedCompetitor(competitor);
    setShowCompetitorPicker(false);
    setIsLoadingContext(true);
    setScrapeStep("gather");
    setChatContext("");

    const domain = competitor.domain;

    try {
      setScrapeStep("gather");
      const res = await fetch(`${WORKER_BASE}/chat/context`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ domain }),
      });

      if (!res.ok) {
        const problem = await res.json().catch(() => null) as { message?: string } | null;
        toast.error(problem?.message ?? `Couldn't read ${domain}. Try again in a minute.`);
        return;
      }

      setScrapeStep("compile");
      const data = await res.json() as { pagesScraped: number; context: string };

      setChatContext(data.context);

      setScrapeStep("ready");
      await new Promise(r => setTimeout(r, 500));

      setMessages([]);
      setStreamingMsg(null);

      const sessionRes = await fetch(`${WORKER_BASE}/chat/sessions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ domain, title: `Chat about ${competitor.name}` }),
      }).catch(() => null);

      if (!sessionRes?.ok) {
        toast.error("Couldn't start a chat. Try again.");
        return;
      }
      const { id: newSessionId } = await sessionRes.json() as { id: string };
      setSessionDomain(domain);
      activeSessionIdRef.current = newSessionId;
      setActiveSessionId(newSessionId);

      const client = getOrCreateClient(newSessionId);

      // Auto-send opening summary
      if (data.context) {
        const summaryText = `Give me a brief summary of what ${competitor.name} does, who they target, and their key value proposition.`;
        const summaryMsg: UIMessage = {
          id: crypto.randomUUID(),
          role: "user",
          parts: [{ type: "text", text: summaryText }],
        };
        setMessages([summaryMsg]);
        setStatus("streaming");
        client.sendMessage(summaryText);
      }

      chatApi.sessions().then(r => { if ("data" in r && r.data) setSessions(r.data as Session[]); });
    } catch (e) {
      console.error("Load context error:", e);
    } finally {
      setScrapeStep(null);
      setIsLoadingContext(false);
    }
  };

  // ── Send message ────────────────────────────────────────────────────────────

  const handleSend = () => {
    if (!input.trim() || status === "streaming") return;
    if (chatUsage && !chatUsage.unlimited && (chatUsage.remaining ?? 0) <= 0) {
      toast.error("You're out of credits for this month. Upgrade for more, or wait for your credits to refill.");
      return;
    }
    const text = input.trim();
    setInput("");
    setStatus("streaming");

    const sessionId = activeSessionIdRef.current;
    if (!sessionId) {
      toast.error("Pick a competitor to start a chat.");
      return;
    }

    // Add user message to local state immediately so it shows right away.
    const optimisticId = `opt-${Date.now()}`;
    setMessages(prev => [...prev, {
      id: optimisticId,
      role: "user" as const,
      parts: [{ type: "text", text }],
    }]);

    // Increment quota on the server first, then send via WebSocket
    chatApi.incrementUsage().then((usageRes) => {
      if ("error" in usageRes) {
        // Quota exceeded or other error — roll back optimistic message
        setMessages(prev => prev.filter(m => m.id !== optimisticId));
        setStatus("idle");
        toast.error(usageRes.error?.message ?? "Usage error");
        return;
      }

      // Update local usage display
      if ("data" in usageRes && usageRes.data) setChatUsage(usageRes.data);

      // Persist the user message; the first one also names the session
      fetch(`${WORKER_BASE}/chat/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ sessionId, role: "user", content: text }),
      })
        .then(async (res) => {
          if (!res.ok) return;
          const saved = await res.json() as { title?: string };
          if (saved.title) setSessions(prev => prev.map(x => x.id === sessionId ? { ...x, title: saved.title!, updated_at: new Date().toISOString() } : x));
        })
        .catch(() => {});

      // The agent looks up the workspace and competitor from the session itself
      getOrCreateClient(sessionId).sendMessage(text);
    });
  };

  // ── Load past session ───────────────────────────────────────────────────────

  const loadSession = async (session: Session) => {
    setSessionLoading(true);
    const res = await chatApi.sessionMessages(session.id);
    if (!("data" in res) || !res.data) { setSessionLoading(false); return; }

    setActiveSessionId(session.id);
    activeSessionIdRef.current = session.id;
    setSessionDomain(session.domain);

    // Load D1 messages immediately for display
    const uiMessages: UIMessage[] = res.data.messages.map(m => ({
      id: m.id,
      role: m.role as "user" | "assistant",
      parts: [{ type: "text", text: m.content }],
      createdAt: m.created_at,
    }));
    setMessages(uiMessages);
    setStreamingMsg(null);
    setSessionLoading(false);

    // Connect agent AFTER setting D1 messages.
    // The onMessages guard prevents an empty cold-start broadcast from wiping them.
    getOrCreateClient(session.id);

    // Reload context from cache
    const ctxRes = await fetch(`${WORKER_BASE}/chat/context`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ domain: session.domain }),
    }).catch(() => null);
    if (ctxRes?.ok) {
      const ctxData = await ctxRes.json() as { context: string };
      setChatContext(ctxData.context);
    }
  };

  const deleteSession = async (id: string) => {
    await chatApi.deleteSession(id);
    setSessions(prev => prev.filter(s => s.id !== id));
    if (activeSessionId === id) {
      setActiveSessionId(undefined);
      activeSessionIdRef.current = undefined;
      setMessages([]);
      setSelectedCompetitor(null);
      setSessionDomain(null);
    }
  };

  useEffect(() => {
    if (!requestedCompetitor || urlSessionId || openedRequestedRef.current || competitors.length === 0) return;
    const match = competitors.find((c) => c.domain === requestedCompetitor);
    if (!match) return;
    openedRequestedRef.current = true;
    void handleSelectCompetitor(match);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competitors, requestedCompetitor, urlSessionId]);

  // The competitor for the open session. Sessions and competitors load in
  // parallel, so this runs again once the competitor list arrives.
  useEffect(() => {
    if (!sessionDomain) return;
    const domain = normalizeDomain(sessionDomain);
    const match = competitors.find((c) => normalizeDomain(c.domain) === domain);
    setSelectedCompetitor((current) => {
      if (match) return match;
      if (current && normalizeDomain(current.domain) === domain) return current;
      // Removed from the watchlist since: keep the chat usable
      return {
        id: `session-${domain}`,
        workspaceId: "",
        name: domain,
        domain,
        faviconUrl: null,
        createdAt: "",
        urlCount: 0,
      };
    });
  }, [sessionDomain, competitors]);

  const hasContext = !!selectedCompetitor && chatContext.length > 0;
  const isLoading = status === "streaming";
  const showChatArea = sessionLoading || hasContext || messages.length > 0 || streamingMsg !== null;

  return (
    <div className="flex h-full flex-col">
      <PageHeader title="Chat" description="Ask questions about any competitor" />

      <div className="flex flex-1 overflow-hidden">
        {/* ── Left sidebar ── */}
        <div className="w-72 shrink-0 flex flex-col border-r border-border overflow-hidden">

          {/* ── Usage indicator ── */}
          {chatUsage && !chatUsage.unlimited && (
            <div className="px-4 py-3 border-b border-border shrink-0 space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Credits · 1 per message</span>
                <span className={cn(
                  "font-mono text-[11px]",
                  (chatUsage.remaining ?? 0) === 0 ? "text-destructive" :
                  (chatUsage.remaining ?? 0) <= 3 ? "text-amber-400" : "text-muted-foreground"
                )}>
                  {(chatUsage.remaining ?? 0).toLocaleString()} left
                </span>
              </div>
              <div className="h-1 w-full rounded-full bg-muted overflow-hidden">
                <div
                  className={cn(
                    "h-full rounded-full transition-all",
                    (chatUsage.remaining ?? 0) === 0 ? "bg-destructive" :
                    (chatUsage.remaining ?? 0) <= 3 ? "bg-amber-400" : "bg-foreground"
                  )}
                  style={{ width: `${Math.min(100, ((chatUsage.remaining ?? 0) / (chatUsage.limit ?? 1)) * 100)}%` }}
                />
              </div>
              {(chatUsage.remaining ?? 0) === 0 ? (
                <a href="/settings?tab=billing" className="block font-sans text-[11px] text-destructive hover:underline">
                  Out of credits — upgrade to continue
                </a>
              ) : (chatUsage.remaining ?? 0) <= 3 ? (
                <p className="font-sans text-[11px] text-amber-400">
                  {chatUsage.remaining} credit{chatUsage.remaining !== 1 ? "s" : ""} left this month
                </p>
              ) : null}
            </div>
          )}

          {hasContext && (
            <details className="border-b border-border shrink-0 group">
              <summary className="flex items-center justify-between px-4 py-2.5 cursor-pointer list-none hover:bg-accent transition-colors">
                <div className="flex items-center gap-2">
                  {selectedCompetitor && (
                    <img src={`https://www.google.com/s2/favicons?domain=${selectedCompetitor.domain}&sz=16`} alt="" className="h-3.5 w-3.5 rounded" />
                  )}
                  <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                    {selectedCompetitor?.name ?? "Context"}
                  </span>
                </div>
                <Icon name="arrow-down-01" size={10} className="text-muted-foreground transition-transform group-open:rotate-180" />
              </summary>
              <div className="px-4 pt-2 pb-3 space-y-1">
                <p className="font-mono text-[11px] text-muted-foreground">{selectedCompetitor?.domain}</p>
                <p className="font-sans text-xs text-muted-foreground">{(chatContext.length / 1000).toFixed(1)}k chars scraped</p>
              </div>
            </details>
          )}

          <div className={cn("flex min-h-0 flex-col", historyOpen && "flex-1")}>
            <div
              role="button"
              tabIndex={0}
              aria-expanded={historyOpen}
              onClick={() => setHistoryOpen(o => !o)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setHistoryOpen(o => !o); } }}
              className="flex items-center justify-between px-4 py-2.5 cursor-pointer hover:bg-accent transition-colors border-b border-border shrink-0"
            >
              <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">History</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMessages([]); setStreamingMsg(null); setSelectedCompetitor(null); setSessionDomain(null); setChatContext(""); setActiveSessionId(undefined); activeSessionIdRef.current = undefined; }}
                  className="font-mono text-[11px] text-muted-foreground hover:text-foreground transition-colors border border-border rounded px-1.5 py-0.5"
                  title="New chat"
                >
                  + New
                </button>
                <Icon name="arrow-down-01" size={10} className={cn("text-muted-foreground transition-transform", historyOpen && "rotate-180")} />
              </div>
            </div>
            {historyOpen && (
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-2 space-y-0.5">
              {sessionsLoading ? (
                <div className="space-y-2 px-2 py-2">
                  {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
                </div>
              ) : sessions.length === 0 ? (
                <p className="px-3 py-3 font-sans text-xs text-muted-foreground">No conversations yet</p>
              ) : sessions.map((s) => (
                <div key={s.id}
                  className={cn(
                    "group/item flex items-start gap-2 rounded-lg px-2.5 py-2 cursor-pointer transition-colors",
                    activeSessionId === s.id
                      ? "bg-muted"
                      : "hover:bg-accent/50"
                  )}
                  onClick={() => loadSession(s)}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-sans text-xs truncate text-foreground">
                      {s.title}
                    </p>
                    <p className="font-mono text-[11px] mt-0.5 text-muted-foreground">
                      {s.domain} · {timeAgo(s.updated_at)}
                    </p>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteSession(s.id); }}
                    className="opacity-0 group-hover/item:opacity-100 transition-opacity shrink-0 mt-0.5 text-muted-foreground hover:text-destructive"
                  >
                    <Icon name="delete-01" size={12} />
                  </button>
                </div>
              ))}
            </div>
            )}
          </div>
        </div>

        {/* ── Chat area ── */}
        <div className="flex flex-1 flex-col overflow-hidden">
          {!showChatArea ? (
            /* ── Empty state: competitor picker ── */
            <div className="flex flex-1 items-center justify-center">
              <div className="text-center space-y-6 max-w-lg w-full px-6">
                <div className="space-y-1">
                  <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                    Select a competitor to chat about
                  </p>
                </div>

                {/* Scraping steps shown while loading */}
                {scrapeStep ? (
                  <div className="space-y-1.5">
                    {SCRAPE_STEPS.map((step, i) => {
                      const activeIndex = SCRAPE_STEPS.findIndex(s => s.id === scrapeStep);
                      const isDone = i < activeIndex;
                      const isActive = step.id === scrapeStep;
                      return (
                        <div key={step.id} className={cn(
                          "flex items-center gap-3 rounded-lg px-3 py-2 transition-all duration-500",
                          isActive ? "bg-foreground/5 border border-border" : isDone ? "opacity-60" : "opacity-25"
                        )}>
                          <div className={cn(
                            "h-4 w-4 rounded-full flex items-center justify-center shrink-0",
                            isDone ? "bg-foreground" : isActive ? "border-2 border-foreground" : "border border-border"
                          )}>
                            {isDone ? <Icon name="tick-01" size={8} className="text-background" />
                              : isActive ? <span className="h-1 w-1 rounded-full bg-foreground animate-pulse" />
                              : null}
                          </div>
                          <span className={cn("font-mono text-[11px] uppercase tracking-widest flex-1",
                            isActive ? "text-foreground" : "text-muted-foreground")}>
                            {step.label}
                          </span>
                          {isActive && <Icon name="refresh" size={9} className="text-muted-foreground animate-spin shrink-0" />}
                        </div>
                      );
                    })}
                  </div>
                ) : competitors.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-border p-8 text-center">
                    <Icon name="radar-01" size={24} className="text-muted-foreground/30 mx-auto mb-3" />
                    <p className="font-sans text-xs text-muted-foreground mb-2">No competitors yet</p>
                    <p className="font-sans text-xs text-muted-foreground">
                      Add competitors in the <a href="/watchlist" className="underline underline-offset-2">Watchlist</a> first.
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {competitors.map((c) => (
                      <button
                        key={c.id}
                        onClick={() => handleSelectCompetitor(c)}
                        disabled={isLoadingContext}
                        className="flex items-center gap-3 rounded-lg border border-border px-4 py-3 hover:bg-accent hover:border-foreground/20 transition-colors text-left disabled:opacity-50"
                      >
                        <img
                          src={`https://www.google.com/s2/favicons?domain=${c.domain}&sz=32`}
                          alt=""
                          className="h-6 w-6 rounded shrink-0"
                          onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                        />
                        <div className="min-w-0">
                          <p className="font-sans text-xs font-medium truncate">{c.name}</p>
                          <p className="font-mono text-[11px] text-muted-foreground truncate">{c.domain}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                <div className="flex flex-wrap gap-2 justify-center pt-2">
                  {["What are their pricing tiers?", "Who are they targeting?", "What features do they highlight?"].map((q) => (
                    <span key={q} className="rounded-lg border border-border px-3 py-1.5 font-sans text-xs text-muted-foreground">{q}</span>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-1 flex-col overflow-hidden min-h-0">
              {/* Session loading skeleton */}
              {sessionLoading ? (
                <div className="flex flex-1 items-center justify-center">
                  <SquareLoader />
                </div>
              ) : (
              <>
              {/* Messages — fills available space, scrolls internally */}
              <div className="relative flex-1 overflow-hidden min-h-0">
                <ChatMessages
                  messages={messages}
                  streamingMsg={streamingMsg}
                  status={status}
                  liveTools={liveTools}
                  onEditMessage={(msgId, newText) => {
                    // Just update the message text locally — no resend
                    setMessages(prev => prev.map(m =>
                      m.id === msgId
                        ? { ...m, parts: [{ type: "text", text: newText }] }
                        : m
                    ));
                  }}
                  onRetry={(msgId, role) => {
                    if (role === "assistant") {
                      // Regenerate: find the last user message before this and resend it
                      const idx = messages.findIndex(m => m.id === msgId);
                      const lastUser = [...messages.slice(0, idx)].reverse().find(m => m.role === "user");
                      if (!lastUser) return;
                      const text = lastUser.parts?.filter(p => p.type === "text").map(p => (p as { text?: string }).text ?? "").join("") ?? "";
                      if (!text) return;
                      const sessionId = activeSessionIdRef.current;
                      if (!sessionId) return;
                      setStatus("streaming");
                      getOrCreateClient(sessionId).sendMessage(text);
                    } else {
                      // Retry user message: resend it
                      const msg = messages.find(m => m.id === msgId);
                      if (!msg) return;
                      const text = msg.parts?.filter(p => p.type === "text").map(p => (p as { text?: string }).text ?? "").join("") ?? "";
                      if (!text) return;
                      const sessionId = activeSessionIdRef.current;
                      if (!sessionId) return;
                      setStatus("streaming");
                      getOrCreateClient(sessionId).sendMessage(text);
                    }
                  }}
                />
                {/* Fade at bottom so messages scroll under the input */}
                <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-background to-transparent" />
              </div>

              {messages.length <= 1 && (
                <div className="px-6 pb-2 flex flex-wrap gap-2 max-w-3xl mx-auto w-full">
                  {["What are their pricing tiers?", "Who are they targeting?", "What features do they highlight?", "Are they hiring?"].map((q) => (
                    <button key={q} onClick={() => setInput(q)}
                      className="rounded-lg border border-border px-3 py-1.5 font-sans text-xs text-muted-foreground hover:bg-accent hover:text-foreground transition-colors">
                      {q}
                    </button>
                  ))}
                </div>
              )}

              <div className="px-4 pb-4 shrink-0">
                <div className="max-w-3xl mx-auto">
                  {/* Textarea box with competitor selector inside bottom-left */}
                  <div className={cn(
                    "relative rounded border border-border bg-background focus-within:border-foreground/40 transition-colors",
                    isLoading && "opacity-60 pointer-events-none"
                  )}>
                    <textarea
                      value={input}
                      onChange={(e) => {
                        setInput(e.target.value);
                        e.target.style.height = "auto";
                        e.target.style.height = `${Math.min(e.target.scrollHeight, 200)}px`;
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleSend();
                          (e.target as HTMLTextAreaElement).style.height = "auto";
                        }
                      }}
                      placeholder={isLoading ? "Waiting for response…" : `Ask anything about ${selectedCompetitor?.name ?? "a competitor"}...`}
                      disabled={isLoading || !selectedCompetitor}
                      rows={3}
                      className={cn(
                        "w-full resize-none bg-transparent font-sans text-xs text-foreground placeholder:text-muted-foreground/50 outline-none leading-relaxed max-h-[200px] overflow-y-auto scrollbar-hide px-3 pt-3 pb-1 transition-opacity",
                        isLoading && "opacity-50 cursor-not-allowed"
                      )}
                    />

                    {/* Bottom bar: competitor selector left, send button right */}
                    <div className="flex items-center justify-between px-2 pb-2 pt-1">
                      {/* Competitor selector */}
                      <div className="relative">
                        <button
                          onClick={() => !activeSessionId && setShowCompetitorPicker(p => !p)}
                          disabled={!!activeSessionId}
                          className={cn(
                            "flex items-center gap-1.5 rounded-md border border-border px-2 py-1 transition-colors",
                            activeSessionId
                              ? "opacity-60 cursor-default"
                              : "hover:bg-accent"
                          )}
                          title={activeSessionId ? "Start a new chat to change competitor" : "Select competitor"}
                        >
                          {selectedCompetitor ? (
                            <img src={`https://www.google.com/s2/favicons?domain=${selectedCompetitor.domain}&sz=16`} alt="" className="h-3 w-3 rounded" />
                          ) : (
                            <Icon name="radar-01" size={10} className="text-muted-foreground" />
                          )}
                          <span className="font-mono text-[11px] text-foreground max-w-[120px] truncate">
                            {selectedCompetitor?.name ?? "Select competitor"}
                          </span>
                          <Icon name="arrow-down-01" size={8} className="text-muted-foreground shrink-0" />
                        </button>

                        {showCompetitorPicker && (
                          <div className="absolute bottom-full mb-1 left-0 z-10 bg-card border border-border rounded-lg shadow-lg py-1 min-w-[200px]">
                            {competitors.map((c) => (
                              <button
                                key={c.id}
                                onClick={() => handleSelectCompetitor(c)}
                                className="flex items-center gap-2.5 w-full px-3 py-2 hover:bg-accent transition-colors text-left"
                              >
                                <img src={`https://www.google.com/s2/favicons?domain=${c.domain}&sz=16`} alt="" className="h-4 w-4 rounded shrink-0" />
                                <div className="min-w-0">
                                  <p className="font-sans text-xs font-medium truncate">{c.name}</p>
                                  <p className="font-mono text-[11px] text-muted-foreground truncate">{c.domain}</p>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Send button */}
                      <Button
                        onClick={() => {
                          handleSend();
                          const ta = document.querySelector<HTMLTextAreaElement>("textarea");
                          if (ta) ta.style.height = "auto";
                        }}
                        disabled={isLoading || !input.trim() || !selectedCompetitor}
                        size="sm"
                        className="shrink-0 gap-1.5 px-2.5"
                      >
                        <Icon name="sent" size={12} />
                        <span className="font-mono text-[11px]">Send</span>
                      </Button>
                    </div>
                  </div>
                  <p className="mt-1.5 text-center font-mono text-[11px] text-muted-foreground">
                    Enter ↵ to send · Shift+Enter for new line
                  </p>
                </div>
              </div>
              </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

